import type { ResearchTone } from "../data/research";

export function Sparkline({ values, tone = "up" }: { values: number[]; tone?: ResearchTone }) {
  const width = 92;
  const height = 28;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((value, index) => {
    const x = (index / Math.max(1, values.length - 1)) * width;
    const y = height - 3 - ((value - min) / range) * (height - 7);
    return `${x},${y}`;
  }).join(" ");
  return <svg className={`research-sparkline spark-${tone}`} viewBox={`0 0 ${width} ${height}`} aria-hidden="true"><polyline points={points}/><circle cx={width} cy={points.split(" ").at(-1)?.split(",")[1]} r="2.2"/></svg>;
}

export function FinancialTrendChart({ data, label = "公司" }: { data: Array<{ period: string; revenue: number; profit: number; roe: number }>; label?: string }) {
  const chart = { left: 46, right: 330, top: 26, bottom: 174 };
  const maxRevenue = Math.max(...data.map((item) => item.revenue)) * 1.15;
  const maxProfit = Math.max(...data.map((item) => item.profit)) * 1.18;
  const centers = data.map((_, index) => chart.left + 48 + index * 92);
  const profitPoints = data.map((item, index) => `${centers[index]},${chart.bottom - (item.profit / maxProfit) * 122}`).join(" ");
  return <div className="financial-chart">
    <div className="chart-legend"><span><i className="legend-revenue"/>营业收入</span><span><i className="legend-profit"/>归母净利润</span><span>单位：亿元</span></div>
    <svg viewBox="0 0 380 215" role="img" aria-label={`${label}营业收入和归母净利润趋势`}>
      {[0, 1, 2, 3].map((line) => <line key={line} x1={chart.left} x2={chart.right} y1={chart.top + line * 49} y2={chart.top + line * 49} className="grid-line"/>)}
      {data.map((item, index) => {
        const barHeight = (item.revenue / maxRevenue) * 132;
        return <g key={item.period}>
          <rect x={centers[index] - 20} y={chart.bottom - barHeight} width="40" height={barHeight} rx="2" className="revenue-bar"/>
          <text x={centers[index]} y={chart.bottom - barHeight - 7} textAnchor="middle" className="bar-value">{item.revenue.toFixed(0)}</text>
          <text x={centers[index]} y="197" textAnchor="middle" className="axis-value">{item.period}</text>
        </g>;
      })}
      <polyline points={profitPoints} className="profit-line"/>
      {data.map((item, index) => {
        const y = chart.bottom - (item.profit / maxProfit) * 122;
        return <g key={`${item.period}-profit`}><circle cx={centers[index]} cy={y} r="4" className="profit-point"/><text x={centers[index]} y={y - 10} textAnchor="middle" className="profit-value">{item.profit.toFixed(1)}</text></g>;
      })}
    </svg>
  </div>;
}

export function BusinessMixDonut({ items, totalLabel }: { items: Array<{ name: string; share: number; growth: number; color: string }>; totalLabel: string }) {
  const normalized = items.map((item) => ({ ...item, visualShare: item.share / items.reduce((sum, current) => sum + current.share, 0) * 100 }));
  let cursor = 0;
  const stops = normalized.map((item) => {
    const start = cursor;
    cursor += item.visualShare;
    return `${item.color} ${start}% ${cursor}%`;
  }).join(", ");
  return <div className="business-mix">
    <div className="donut" style={{ background: `conic-gradient(${stops})` }}><div><strong>{totalLabel.split(" ")[0]}</strong><span>{totalLabel.split(" ").slice(1).join(" ")}</span></div></div>
    <div className="mix-list">{items.map((item) => <div key={item.name}><i style={{ background: item.color }}/><span>{item.name}</span><strong>{item.share.toFixed(item.share % 1 ? 1 : 0)}%</strong><em className={item.growth > 0 ? "tone-up" : item.growth < 0 ? "tone-down" : "tone-neutral"}>{item.growth === 0 ? "披露值" : `${item.growth > 0 ? "+" : ""}${item.growth}%`}</em></div>)}</div>
  </div>;
}

export function PositionBars({ items }: { items: Array<{ metric: string; value: number; display?: string; rank: string; source: string }> }) {
  return <div className="position-bars">{items.map((item) => <div className="position-row" key={item.metric}>
    <div className="position-copy"><strong>{item.metric}</strong><span>{item.source}</span></div>
    <div className="position-scale"><div className="position-track"><i style={{ width: `${item.value}%` }}/></div><span>相对强度 {item.value}</span></div>
    <div className="position-result"><strong>{item.rank}</strong><span>{item.display ?? "待核验"}</span></div>
  </div>)}</div>;
}

export function EarningsForecast({ items }: { items: Array<{ period: string; profit: number; growth: number }> }) {
  const max = Math.max(...items.map((item) => item.profit));
  return <div className="earnings-forecast">{items.map((item) => <div key={item.period}>
    <span>{item.period}</span><div><i style={{ height: `${Math.max(18, item.profit / max * 100)}%` }}/><b>{item.profit.toFixed(1)}</b></div><em className={item.growth >= 0 ? "tone-up" : "tone-down"}>{item.growth >= 0 ? "+" : ""}{item.growth.toFixed(1)}%</em>
  </div>)}</div>;
}

export function DemandBars({ items }: { items: Array<{ label: string; base: number; next: number; note: string }> }) {
  const max = Math.max(...items.map((item) => item.next));
  return <div className="demand-bars">{items.map((item) => <div className="demand-row" key={item.label}>
    <div><strong>{item.label}</strong><span>{item.note}</span></div>
    <div className="demand-track"><i className="demand-base" style={{ width: `${(item.base / max) * 100}%` }}/><i className="demand-next" style={{ width: `${(item.next / max) * 100}%` }}/></div>
    <b>{item.next}</b>
  </div>)}</div>;
}

export function ResearchScore({ value, label }: { value: number; label: string }) {
  const normalized = value > 0 && value <= 10 ? value * 10 : value;
  return <div className="research-score" style={{ "--score": `${normalized * 3.6}deg` } as React.CSSProperties}><div><strong>{normalized}</strong><span>/ 100</span></div><small>{label}</small></div>;
}
